import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "sonner";
import { Plus, Search, Building2, MapPin } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const APPROVAL_LABELS = { not_started: "Not started", pending: "Pending", approved: "Approved", denied: "Denied" };
const APPROVAL_TONE = { not_started: "gray", pending: "orange", approved: "green", denied: "red" };

const empty = {
  name: "", address: "", city: "", state: "", zip: "", phone: "",
  np_id: "", medical_director_id: "", cna_contact_id: "",
  dor_name: "", dor_phone: "", dor_email: "",
  np_approval_status: "not_started", start_date: "",
  patient_count: 0, visits_per_month: 0, visits_per_week: 0,
  notes: "",
};

export default function FacilitiesPage() {
  const [facs, setFacs] = useState([]);
  const [nps, setNps] = useState([]);
  const [mds, setMds] = useState([]);
  const [cnas, setCnas] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, n, m, c] = await Promise.all([
        api.get("/facilities"), api.get("/nps"),
        api.get("/medical_directors"), api.get("/cnas"),
      ]);
      setFacs(f.data); setNps(n.data); setMds(m.data); setCnas(c.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[facilities] load failed:", e?.message || e);
      toast.error("Could not load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allStates = Array.from(new Set(facs.map((f) => (f.state || "").trim().toUpperCase()).filter(Boolean))).sort();

  const filtered = facs.filter((f) => {
    if (filter !== "all" && f.status_light !== filter) return false;
    if (stateFilter !== "all" && (f.state || "").trim().toUpperCase() !== stateFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      f.name?.toLowerCase().includes(s) ||
      f.city?.toLowerCase().includes(s) ||
      f.np?.name?.toLowerCase().includes(s)
    );
  });

  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestFacility, setSuggestFacility] = useState(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [applyingSuggest, setApplyingSuggest] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        np_id: form.np_id || null,
        medical_director_id: form.medical_director_id || null,
        cna_contact_id: form.cna_contact_id || null,
        patient_count: Number(form.patient_count) || 0,
        visits_per_month: Number(form.visits_per_month) || 0,
        visits_per_week: Number(form.visits_per_week) || 0,
      };
      const { data: created } = await api.post("/facilities", payload);
      toast.success("Facility added");
      setOpen(false); setForm(empty); load();
      // Auto-suggest NP matches only when saved without an NP assigned
      if (!payload.np_id && (payload.city || payload.state || payload.address)) {
        setSuggestFacility(created);
        setSuggestOpen(true);
        setLoadingSuggest(true);
        try {
          const { data } = await api.post("/coverage/suggest-for-facility", {
            facility_name: created?.name,
            address: created?.address,
            city: created?.city,
            state: created?.state,
            zip: created?.zip,
          });
          setSuggestions(data?.suggestions || []);
        } catch { setSuggestions([]); }
        finally { setLoadingSuggest(false); }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const acceptSuggestion = async (npId) => {
    if (!suggestFacility) return;
    setApplyingSuggest(true);
    try {
      await api.put(`/facilities/${suggestFacility.id}`, { np_id: npId });
      toast.success("NP assigned");
      setSuggestOpen(false);
      setSuggestFacility(null);
      setSuggestions([]);
      load();
    } catch { toast.error("Could not assign"); }
    finally { setApplyingSuggest(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Operations</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Facilities</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">{facs.length} total · click any row to open its folder.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#4A6B5D] hover:bg-[#3E594D] hover:-translate-y-[1px] transition-transform" data-testid="add-facility-btn">
          <Plus className="h-4 w-4 mr-2" /> Add facility
        </Button>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847A]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, city, NP..." className="pl-9 h-11 bg-white border-[#E5E0D8]" data-testid="facility-search" />
        </div>
        <div className="flex gap-2 text-xs">
          {[
            ["all", "All", "gray"], ["red", "Attention", "red"],
            ["orange", "Soon", "orange"], ["green", "Normal", "green"],
          ].map(([v, l]) => (
            <button
              key={v} onClick={() => setFilter(v)}
              className={`px-3 py-2 rounded-md border transition-colors ${filter === v ? "bg-[#4A6B5D] text-white border-[#4A6B5D]" : "bg-white border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}
              data-testid={`filter-${v}`}
            >{l}</button>
          ))}
        </div>
      </div>

      {allStates.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="facility-state-filter">
          <span className="text-[10px] uppercase tracking-[0.06em] text-[#8A847A]">State</span>
          <button
            onClick={() => setStateFilter("all")}
            className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase transition-colors ${stateFilter === "all" ? "bg-[#4A6B5D] text-white" : "bg-white border border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}
            data-testid="facility-state-filter-all"
          >All</button>
          {allStates.map((st) => {
            const count = facs.filter((f) => (f.state || "").trim().toUpperCase() === st).length;
            return (
              <button
                key={st}
                onClick={() => setStateFilter(st)}
                className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase transition-colors ${stateFilter === st ? "bg-[#4A6B5D] text-white" : "bg-white border border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}
                data-testid={`facility-state-filter-${st}`}
              >{st} ({count})</button>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="mt-6 hidden md:block bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
        <table className="w-full" data-testid="facilities-table">
          <thead>
            <tr className="bg-[#F7F5F0]">
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] font-semibold text-[#5C5850]">Status</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] font-semibold text-[#5C5850]">Facility</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] font-semibold text-[#5C5850]">NP</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] font-semibold text-[#5C5850]">MD Approval</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.05em] font-semibold text-[#5C5850]">Patients</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.05em] font-semibold text-[#5C5850]">Open issues</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-14 text-[#8A847A] text-sm">
                <Building2 className="h-8 w-8 mx-auto mb-2 opacity-60" />
                No facilities match your view.
              </td></tr>
            )}
            {filtered.map((f) => (
              <tr key={f.id} className="border-t border-[#E5E0D8] table-row-hover cursor-pointer" data-testid={`facility-row-${f.id}`} onClick={(e) => { if (e.target.closest("a")) return; window.location.href = `/facilities/${f.id}`; }}>
                <td className="px-4 py-3"><Link to={`/facilities/${f.id}`}><StatusPill status={f.status_light} label={f.status_light === "red" ? "Attention" : f.status_light === "orange" ? "Soon" : "Normal"} /></Link></td>
                <td className="px-4 py-3">
                  <Link to={`/facilities/${f.id}`} className="font-medium text-[#292723] hover:text-[#4A6B5D]">{f.name}</Link>
                  <div className="text-xs text-[#8A847A]">{[f.city, f.state].filter(Boolean).join(", ") || "—"}</div>
                </td>
                <td className="px-4 py-3 text-sm">{f.np?.name || <span className="text-[#8A847A]">Unassigned</span>}</td>
                <td className="px-4 py-3"><StatusPill status={APPROVAL_TONE[f.np_approval_status] || "gray"} label={APPROVAL_LABELS[f.np_approval_status] || "—"} /></td>
                <td className="px-4 py-3 text-sm text-right font-mono">{f.patient_count || 0}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{f.open_issues_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-6 md:hidden space-y-3">
        {filtered.map((f) => (
          <Link key={f.id} to={`/facilities/${f.id}`} className="block bg-white border border-[#E5E0D8] rounded-md p-4" data-testid={`facility-card-${f.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{f.name}</div>
                <div className="text-xs text-[#8A847A]">{f.np?.name || "Unassigned NP"}</div>
              </div>
              <StatusPill status={f.status_light} label={f.status_light === "red" ? "!" : f.status_light === "orange" ? "~" : "ok"} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
              <div><div className="text-[10px] uppercase text-[#8A847A]">Patients</div><div className="font-mono font-semibold">{f.patient_count || 0}</div></div>
              <div><div className="text-[10px] uppercase text-[#8A847A]">Vis/mo</div><div className="font-mono font-semibold">{f.visits_per_month || 0}</div></div>
              <div><div className="text-[10px] uppercase text-[#8A847A]">Issues</div><div className="font-mono font-semibold">{f.open_issues_count || 0}</div></div>
            </div>
          </Link>
        ))}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="heading text-2xl">Add facility</SheetTitle>
            <SheetDescription>You can fill in the rest inside the facility folder.</SheetDescription>
          </SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="add-facility-form">
            <FieldRow>
              <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="fac-name" />
            </FieldRow>
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Address</Label>
              <div className="mt-1.5">
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  onSelect={(s) => {
                    setForm((f) => ({
                      ...f,
                      address: s.street || s.label,
                      city: s.city || f.city,
                      state: s.state || f.state,
                      zip: s.postcode || f.zip,
                    }));
                  }}
                  testId="fac-address"
                  placeholder="Start typing a facility address…"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
              <Field label="Zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
            </div>
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />

            <SelectField label="Assigned NP" value={form.np_id} onChange={(v) => setForm({ ...form, np_id: v })} options={[{ value: "", label: "— none —" }, ...nps.map((n) => ({ value: n.id, label: n.name }))]} testId="fac-np" />
            <SelectField label="Medical Director" value={form.medical_director_id} onChange={(v) => setForm({ ...form, medical_director_id: v })} options={[{ value: "", label: "— none —" }, ...mds.map((m) => ({ value: m.id, label: m.name }))]} testId="fac-md" />
            <SelectField label="CNA Contact" value={form.cna_contact_id} onChange={(v) => setForm({ ...form, cna_contact_id: v })} options={[{ value: "", label: "— none —" }, ...cnas.map((c) => ({ value: c.id, label: c.name }))]} testId="fac-cna" />

            <div className="grid grid-cols-3 gap-2">
              <Field label="DOR Name" value={form.dor_name} onChange={(v) => setForm({ ...form, dor_name: v })} />
              <Field label="DOR Phone" value={form.dor_phone} onChange={(v) => setForm({ ...form, dor_phone: v })} />
              <Field label="DOR Email" value={form.dor_email} onChange={(v) => setForm({ ...form, dor_email: v })} />
            </div>

            <SelectField label="NP Approval Status" value={form.np_approval_status} onChange={(v) => setForm({ ...form, np_approval_status: v })} options={Object.entries(APPROVAL_LABELS).map(([v, l]) => ({ value: v, label: l }))} />

            <div className="grid grid-cols-2 gap-2">
              <Field label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
              <Field label="Patient count" type="number" value={form.patient_count} onChange={(v) => setForm({ ...form, patient_count: v })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Visits / month" type="number" value={form.visits_per_month} onChange={(v) => setForm({ ...form, visits_per_month: v })} />
              <Field label="Visits / week" type="number" value={form.visits_per_week} onChange={(v) => setForm({ ...form, visits_per_week: v })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="save-facility-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Auto-suggest NPs after saving a new facility with no NP */}
      <Sheet open={suggestOpen} onOpenChange={(v) => { setSuggestOpen(v); if (!v) { setSuggestFacility(null); setSuggestions([]); } }}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="heading text-2xl">Suggested NP matches</SheetTitle>
            <SheetDescription>
              {suggestFacility?.name ? <>Top NPs for <span className="font-semibold text-[#292723]">{suggestFacility.name}</span> based on geography and current workload.</> : "AI is picking the closest NPs."}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6" data-testid="suggest-sheet">
            {loadingSuggest ? (
              <div className="text-sm text-[#8A847A] flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-full bg-[#4A6B5D] animate-pulse"/> AI thinking…
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-sm text-[#8A847A]">No confident matches. You can assign an NP manually from the facility folder.</div>
            ) : (
              <div className="space-y-3">
                {suggestions.map((s, i) => (
                  <div key={s.np_id} className="border border-[#E5E0D8] rounded-md p-4 hover:border-[#4A6B5D] transition-colors" data-testid={`suggest-row-${s.np_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#4A6B5D]">Pick #{i + 1}</span>
                          <span className="text-[10px] text-[#8A847A]">
                            {s.current_load?.facilities ?? 0} facilities · {s.current_load?.visits_per_week ?? 0} vis/wk
                          </span>
                        </div>
                        <div className="mt-1 font-semibold text-[#292723]">{s.np_name}</div>
                        <div className="mt-1 text-xs text-[#5C5850]">{s.reason}</div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => acceptSuggestion(s.np_id)}
                        disabled={applyingSuggest}
                        className="bg-[#4A6B5D] hover:bg-[#3E594D] flex-shrink-0"
                        data-testid={`suggest-accept-${s.np_id}`}
                      >Assign</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setSuggestOpen(false)}>Skip for now</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FieldRow({ children }) { return <div>{children}</div>; }

export function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{label}</Label>
      <Input
        required={required} type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-10 bg-white border-[#E5E0D8]"
        data-testid={testId}
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{label}</Label>
      <Select value={value || ""} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger className="mt-1.5 h-10 bg-white border-[#E5E0D8]" data-testid={testId}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {options.map((o) => (
            <SelectItem key={o.value || "__none__"} value={o.value || "__none__"}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
